import { Button, IconButton, notify, Tooltip } from '@affine/component';
import { MenuItem } from '@affine/component/ui/menu';
import { DocService } from '@affine/core/modules/doc';
import {
  EditorService,
  PresentationStateMachine,
  WorkspaceRealtimeService,
} from '@affine/core/modules/editor';
import {
  AccessibleFolderCacheStore,
  type AccessibleFolderMeta,
  createCachedFolderAreaResolver,
} from '@affine/core/modules/organize';
import { useI18n } from '@affine/i18n';
import { CollaborationIcon } from '@blocksuite/icons/rc';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * CP-012: Moderation Button for Detail Page Header (sl-ck8v: Socket-based)
 *
 * Shows three states:
 * 1. Default: "Start Presentation" - grey icon
 * 2. Active (I'm presenting): "Stop Presentation" - primary color, active state
 * 3. Join (someone else presenting in THIS doc): "Join Presentation" - primary color
 */
export const DetailPageHeaderModerationButton = ({
  rail = false,
  railLabel,
  renderOutsideEdgeless = false,
  showTooltip = true,
}: {
  rail?: boolean;
  railLabel?: string;
  renderOutsideEdgeless?: boolean;
  showTooltip?: boolean;
} = {}) => {
  const action = useDetailPageHeaderModerationAction(renderOutsideEdgeless);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelTruncated, setLabelTruncated] = useState(false);

  useEffect(() => {
    const label = labelRef.current;
    if (!label) {
      setLabelTruncated(false);
      return;
    }
    const measureLabel = () =>
      setLabelTruncated(label.scrollWidth > label.clientWidth);
    measureLabel();
    const observer = new ResizeObserver(measureLabel);
    observer.observe(label);
    return () => observer.disconnect();
  }, [railLabel]);

  if (!action) {
    return null;
  }

  const buttonStyle = {
    flexShrink: 0,
    color:
      !rail && (action.isActive || action.shouldGlow)
        ? 'var(--streamlain-primary-color)'
        : undefined,
    backgroundColor:
      !rail && (action.isActive || action.shouldGlow)
        ? 'var(--streamlain-hover-color-filled)'
        : undefined,
    boxShadow: action.shouldGlow
      ? '0 0 0 3px var(--streamlain-primary-color), 0 0 12px var(--streamlain-primary-color)'
      : undefined,
    borderRadius: rail ? '4px' : '8px',
  };
  const buttonProps = {
    ref: buttonRef,
    style: buttonStyle,
    onClick: action.handleClick,
    disabled: action.disabled,
    'data-active': action.isActive,
    'data-glow': action.shouldGlow,
    'data-coachmark-target': 'moderation-button',
    'data-testid': 'detail-header-moderation-button',
    'data-rail': rail || undefined,
  };

  const button = railLabel ? (
    <Button
      {...buttonProps}
      size="custom"
      variant="custom"
      prefix={<CollaborationIcon />}
      prefixStyle={rail ? { color: 'currentColor' } : undefined}
    >
      <span ref={labelRef} data-rail-action-label>
        {railLabel}
      </span>
    </Button>
  ) : (
    <IconButton
      {...buttonProps}
      size={rail ? '20' : '24'}
      iconStyle={rail ? { color: 'currentColor' } : undefined}
    >
      <CollaborationIcon />
    </IconButton>
  );

  return showTooltip || labelTruncated ? (
    <Tooltip content={action.tooltipText} side={rail ? 'right' : undefined}>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

export const DetailPageHeaderModerationMenuItem = () => {
  const action = useDetailPageHeaderModerationAction();

  if (!action) {
    return null;
  }

  return (
    <MenuItem
      prefixIcon={<CollaborationIcon />}
      onSelect={action.handleClick}
      disabled={action.disabled}
    >
      {action.tooltipText}
    </MenuItem>
  );
};

const useDetailPageHeaderModerationAction = (renderOutsideEdgeless = false) => {
  const t = useI18n();
  const editorService = useService(EditorService);
  const socketService = useService(WorkspaceRealtimeService);
  const stateMachine = useService(PresentationStateMachine);
  const docService = useService(DocService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);

  const isModerating = useLiveData(stateMachine.isModerating$);
  const isFollowing = useLiveData(stateMachine.isFollowing$);
  const mode = useLiveData(editorService.editor.mode$);
  const allPresenters = useLiveData(socketService.availablePresenters$);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(accessibleFolderCache.watchAllAccessibleFolders(), []),
      [accessibleFolderCache]
    )
  );

  // Filter presenters to only those in THIS doc
  const currentDocId = docService.doc.id;
  const availablePresenters = useMemo(
    () => allPresenters.filter(p => p.docId === currentDocId),
    [allPresenters, currentDocId]
  );
  const isPrivateDoc = useMemo(
    () => isDocInPrivateArea(currentDocId, accessibleFolders),
    [accessibleFolders, currentDocId]
  );

  const someoneElsePresenting = availablePresenters.length > 0 && !isModerating;
  const disabledByPrivateArea = isPrivateDoc && !isModerating && !isFollowing;

  const shouldGlow =
    someoneElsePresenting && !isFollowing && !disabledByPrivateArea;
  const isActive = isModerating || someoneElsePresenting || isFollowing;

  const handleClick = useCallback(() => {
    if (disabledByPrivateArea) {
      return;
    }

    if (isModerating) {
      // Stop my presentation
      editorService.editor.toggleModeration();
    } else if (isFollowing) {
      // Stop following
      stateMachine.stopFollowing().catch(console.error);
    } else if (someoneElsePresenting) {
      // Reopen the join prompt instead of entering follow mode immediately.
      const presenter = availablePresenters[0];
      socketService.requestModerationJoinPrompt(
        presenter.docId,
        presenter.sessionId
      );
    } else {
      // Start my presentation - with pre-checks
      const result = editorService.editor.toggleModeration();

      if (!result.success) {
        if (result.error === 'offline') {
          notify.error({
            title: t['com.streamlain.moderation.error.offline.title'](),
            message: t['com.streamlain.moderation.error.offline.message'](),
          });
        } else if (result.error === 'moderator_exists') {
          notify.error({
            title:
              t['com.streamlain.moderation.error.moderator_exists.title'](),
            message: t[
              'com.streamlain.moderation.error.moderator_exists.message'
            ]({
              name: result.existingModeratorName || 'Someone',
            }),
          });
        }
      }
    }
  }, [
    isModerating,
    isFollowing,
    someoneElsePresenting,
    availablePresenters,
    socketService,
    disabledByPrivateArea,
    editorService.editor,
    stateMachine,
    t,
  ]);

  // Only show in Edgeless mode
  if (mode !== 'edgeless' && !renderOutsideEdgeless) {
    return null;
  }

  // Determine tooltip text
  let tooltipText: string;
  if (disabledByPrivateArea) {
    tooltipText = t['com.streamlain.moderation.privateDocDisabled']();
  } else if (isModerating) {
    tooltipText = t['com.streamlain.moderation.button.stop']();
  } else if (isFollowing) {
    tooltipText = t['com.streamlain.moderation.button.stop']();
  } else if (someoneElsePresenting) {
    tooltipText = t['com.streamlain.moderation.button.join']();
  } else {
    tooltipText = t['com.streamlain.moderation.button.start']();
  }

  return {
    handleClick,
    disabled: disabledByPrivateArea,
    isActive,
    shouldGlow,
    tooltipText,
  };
};

function isDocInPrivateArea(
  docId: string,
  folders: readonly AccessibleFolderMeta[]
): boolean {
  const resolveArea = createCachedFolderAreaResolver(folders);
  return folders.some(
    folder => folder.docIds.includes(docId) && resolveArea(folder) === 'private'
  );
}
